import { engineComponent, ArrayUtils, StringUtils, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { AnimatedPaylinePart } from "./AnimatedPaylinePart";
import { BezierPlugin } from "gsap";

@engineComponent(ComponentTypes.DisplayObject)
export class AnimatedPayline extends ContainerElement {
    private _valuesX: Array<number> = [];
    private _valuesY: Array<number> = [];
    private _tints: Array<string> = [];
    private scaleSets: Array<number> = [];
    private _childNode: Element;
    private _pointsCount: number;
    private _bezierPoints: Array<PIXI.Point>;
    private _curviness: number = 1;
    protected _positionOffset: number = 3;
    protected _rotationOffset: number = 3;
    protected _scaleOffset: Array<number> = [1, 1];

    constructor() {
        super();
    }

    public createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute("ValuesX")) {
            this._valuesX = ArrayUtils.numberArray(node.getAttribute("ValuesX"));
        }

        if (node.hasAttribute("ValuesY")) {
            this._valuesY = ArrayUtils.numberArray(node.getAttribute("ValuesY"));
        }

        if (node.hasAttribute("Tints")) {
            this._tints = node.getAttribute("Tints").split(";");
        }

        if (node.hasChildNodes()) {
            this._childNode = node.firstElementChild;
        }

        if (node.hasAttribute("ScaleSets")) {
            let scaleSets: Array<Array<number>> = ArrayUtils.arraysNumbers(node.getAttribute("ScaleSets"), "|");

            for (let i = 0; i < scaleSets.length; i++) {
                this.scaleSets[scaleSets[i][0]] = scaleSets[i][1];
            }
        }

        if (node.hasAttribute("Curviness")) {
            this._curviness = parseFloat(node.getAttribute("Curviness"));
        }

        if (node.hasAttribute("PointsCount")) {
            this._pointsCount = parseInt(node.getAttribute("PointsCount"));
        }

        if (node.hasAttribute("PositionOffset")) {
            this._positionOffset = parseInt(node.getAttribute("PositionOffset"));
        }
        if (node.hasAttribute("RotationOffset")) {
            this._rotationOffset = parseInt(node.getAttribute("RotationOffset"));
        }
        if (node.hasAttribute("ScaleOffset")) {
            this._scaleOffset = ArrayUtils.numberArray(node.getAttribute("ScaleOffset"));
        }

        if (node.hasAttribute("Points")) {
            let pointsData = this.formatPoints(node.getAttribute("Points"));
            let bezierData = BezierPlugin.bezierThrough(pointsData, this._curviness);

            this._bezierPoints = this.getBezierStepPoints(bezierData, this._pointsCount);
        }

        this.createChilds();
    }

    public updateByNode(node: Element) {
        if (node.hasAttribute("Method")) {
            switch (node.getAttribute("Method")) {
                case "stop":
                    this.stop();
                    break;
                case "fishedLow":
                    this.finished(true);
                    break;
                case "fishedHigh":
                    this.finished(false);
                    break;
                case "win":
                    this.win();
                    break;
            }
        }
    }

    public stop(): void {
        this.visible = false;
        for (let i: number = 0; i < this.children.length; i++) {
            (this.children[i] as AnimatedPaylinePart).stop();
        }
    }

    public win(): void {
        this.visible = true;

        for (let i: number = 0; i < this.children.length; i++) {
            (this.children[i] as AnimatedPaylinePart).animateWin(
                i,
                this._scaleOffset,
                this.scaleSets[i] != null ? this.scaleSets[i] : null,
            );
        }
    }

    public finished(isLow: boolean): void {
        this.visible = true;

        for (let i: number = 0; i < this.children.length; i++) {
            (this.children[i] as AnimatedPaylinePart).animateFinished(i, isLow);
        }
    }

    public clone(): AnimatedPayline {
        const element: AnimatedPayline = new AnimatedPayline();
        element.x = this.x;
        element.y = this.y;
        element._valuesX = this._valuesX;
        element._valuesY = this._valuesY;
        element._tints = this._tints;
        element._childNode = this._childNode;
        element.createChilds();
        return element;
    }

    private createChilds(): void {
        if (this._bezierPoints != null && this._bezierPoints.length > 0 && this._childNode != null) {
            for (let i: number = 0; i < this._bezierPoints.length; i++) {
                let part: AnimatedPaylinePart = new AnimatedPaylinePart();
                let randomOffsetX: number = this.getRandom(-this._positionOffset, this._positionOffset);
                let randomOffsetY: number = this.getRandom(-this._positionOffset, this._positionOffset);

                part.x = this._bezierPoints[i].x + randomOffsetX;
                part.y = this._bezierPoints[i].y + randomOffsetY;

                part.createByNode(this._childNode);
                // let randomScale: number = this.getRandom(this._scaleOffset[0], this._scaleOffset[1]);
                let rotation = this.degrees2radians(Math.round(Math.random() * this._rotationOffset));
                part.rotation = Math.round(Math.random()) == 0 ? rotation : -rotation;

                // part.children[0].scale.x = Math.round(Math.random())== 0 ? randomScale : -randomScale;
                // part.children[0].scale.y = randomScale;
                // part.tints(this._tints);
                this.addChild(part);
            }
        }
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    /**
     *
     * @param {string} points
     * @param {boolean} useGlobal
     * @returns {Array<PIXI.Point>}
     */
    private formatPoints(points: string, useGlobal: boolean = false): Array<PIXI.Point> {
        let result: Array<PIXI.Point> = [];
        let regex = /\[(.*?)\]/g;
        let data = points.match(regex);

        for (let i: number = 0; i < data.length; i++) {
            let pointArray = data[i].substring(1, data[i].length - 1).split(",");

            if (pointArray.length == 2) {
                let x = this.formatPoint(pointArray[0], "x", useGlobal);
                let y = this.formatPoint(pointArray[1], "y", useGlobal);

                result.push(new PIXI.Point(x, y));
            }
        }

        return result;
    }

    /**
     *
     * @param {string} value
     * @param {string} id
     * @param {boolean} useGlobal
     * @returns {number}
     */
    private formatPoint(value: string, id: string, useGlobal: boolean = false): number {
        let result: number;
        if (value == "c") {
            result = this[id];
        } else {
            if (useGlobal) {
                result = this[id] + (parseInt(value) - this.getGlobalPosition()[id]);
            } else {
                result = parseInt(value);
            }
        }

        return result;
    }

    /**
     *
     * @param data
     * @param {number} length
     * @returns {Array<PIXI.Point>}
     */
    private getBezierStepPoints(data: any, length: number): Array<PIXI.Point> {
        let segmentLength = length / data.x.length;
        let pointPerSegment: Array<number> = [];
        let bezierStepPoints: Array<PIXI.Point> = [];

        if (data.x.length > 1) {
            segmentLength = Math.round(segmentLength);
            let sum = 0;
            for (let i: number = 0; i < data.x.length - 1; i++) {
                pointPerSegment.push(segmentLength);
                sum += segmentLength;
            }
            pointPerSegment.push(length - sum);
        } else {
            pointPerSegment = [segmentLength];
        }

        let time = 0;
        for (let i: number = 0; i < data.x.length; i++) {
            for (let j: number = 0; j < pointPerSegment[i]; j++) {
                if (i + 1 < data.x.length) {
                    time = j / pointPerSegment[i];
                } else {
                    time = j / (pointPerSegment[i] - 1);
                }
                bezierStepPoints.push(this.bezier(time, data.x[i], data.y[i]));
            }
        }

        return bezierStepPoints;
    }

    /**
     *
     * @param {number} t
     * @param {IBezierData} dataX
     * @param {IBezierData} dataY
     * @returns {PIXI.Point}
     */
    private bezier(t: number, dataX: IBezierData, dataY: IBezierData): PIXI.Point {
        let cX = 3 * dataX.ba,
            bX = 3 * (dataX.c - dataX.b) - cX,
            aX = dataX.da - cX - bX;

        let cY = 3 * dataY.ba,
            bY = 3 * (dataY.c - dataY.b) - cY,
            aY = dataY.da - cY - bY;

        let x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + dataX.a;
        let y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + dataY.a;

        return new PIXI.Point(x, y);
    }
}

export interface IBezierData {
    a: number;
    b: number;
    ba: number;
    c: number;
    ca: number;
    d: number;
    da: number;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/AnimatedPayline.ts
 **/